import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRecipeStore } from '@/stores/recipeStore';
import { RecipeFormData } from '@/types/recipe';
import RecipeFormModern from '@/components/RecipeFormModern';
import { readPendingRecipe, clearPendingRecipe } from '@/lib/pendingRecipe';

const RecipeNewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useAuth();
  const { addRecipe } = useRecipeStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialFormData, setInitialFormData] = useState<Partial<RecipeFormData> | undefined>(undefined);
  const [shouldAutoGenerateImage, setShouldAutoGenerateImage] = useState(false);

  // Check for pre-filled data from assistant/studio.
  // P0.10: the data is NOT consumed on read — it stays in sessionStorage so a
  // refresh re-populates the form, and is only cleared after a successful save.
  useEffect(() => {
    const fromSource = new URLSearchParams(location.search).get('from');
    const isStudio = fromSource === 'studio';

    const pending = readPendingRecipe();

    if (pending) {
      setInitialFormData(pending);
      if ((pending.fromStudio || pending.fromAssistant) && pending.autoGenerateImage) {
        setShouldAutoGenerateImage(true);
      }
      return;
    }

    // No pending data — recover from the studio handoff when possible instead
    // of showing a blank form (covers the old single-consume data loss).
    if (isStudio) {
      const studioData = sessionStorage.getItem('studioRecipeData');
      if (studioData) {
        try {
          const formData = JSON.parse(studioData) as Partial<RecipeFormData> & {
            fromStudio?: boolean;
            studioPersona?: string;
          };
          setInitialFormData(formData);
          return;
        } catch {
          // corrupt — fall through to redirect
        }
      }
      navigate('/studio', { replace: true });
    }
  }, [navigate, location.search]);

  // Check if user has permission to add recipes
  if (role !== 'admin' && role !== 'reader') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="space-y-4">
            <div className="text-6xl opacity-20">🔒</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h1>
            <p className="text-muted-foreground">
              Please sign in to create recipes.
            </p>
            <Button onClick={() => navigate('/')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleFormSubmit = async (formData: RecipeFormData) => {
    try {
      setIsSubmitting(true);
      const newRecipe = await addRecipe(formData);

      // Clean up handoff data only after a successful save
      clearPendingRecipe();
      sessionStorage.removeItem('studioRecipeData');

      // Navigate to the new recipe's detail page
      navigate(`/recipe/${newRecipe.id}`);
    } catch {
      // Error is already handled by the addRecipe function and RecipeForm
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Check if we came from studio (via URL param)
    const searchParams = new URLSearchParams(location.search);
    const fromSource = searchParams.get('from');

    if (fromSource === 'studio') {
      // P0.8: return to the Chef's Table (step 5) when the generated recipe is
      // still available — going back to step 4 risked a second 20-coin charge.
      const hasStudioRecipe = Boolean(sessionStorage.getItem('studioRecipeData'));
      navigate(hasStudioRecipe ? '/studio/step/5' : '/studio');
    } else if (fromSource === 'clone' || fromSource === 'assistant') {
      // Go back to the previous page (the recipe detail page)
      navigate(-1);
    } else {
      // Default: go back to launcher
      navigate('/recipe/new');
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      {/* Compact Back Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCancel}
        disabled={isSubmitting}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      {/* Recipe Form - handles its own title in sticky header */}
      <RecipeFormModern
        onSubmit={handleFormSubmit}
        isEditMode={false}
        onCancel={handleCancel}
        initialData={initialFormData as RecipeFormData | undefined}
        autoGenerateImage={shouldAutoGenerateImage}
      />
    </div>
  );
};

export default RecipeNewPage;
